import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  IconButton,
  Checkbox,
  FormControlLabel,
  TextField,
} from '@mui/material';
import { 
  Refresh as RefreshIcon, 
  Close as CloseIcon, 
  DeleteOutline as DeleteOutlineIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { connectInstanceService, InstanceStatus } from '../../services/connectInstanceService';

interface SSHLogDrawerProps {
  open: boolean;
  instanceId: number | null;
  operationType: 'retry' | 'cleanup' | null;
  isInstall?: boolean; // True if this is a fresh install, false if retry from failed step
  onClose: () => void;
}

const SSHLogDrawer: React.FC<SSHLogDrawerProps> = ({
  open,
  instanceId,
  operationType,
  isInstall = false,
  onClose,
}) => {
  const [status, setStatus] = useState<InstanceStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [clearLogLength, setClearLogLength] = useState<number | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(6);
  const [copied, setCopied] = useState(false);
  const logEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open && instanceId) {
      fetchStatus();
      // Auto-refresh will be controlled by the status check in the other useEffect
      // For completed/failed instances, auto-refresh is disabled
    } else {
      // Reset status when modal closes (but keep clearLogLength so clear persists)
      setStatus(null);
      setAutoRefresh(true);
    }
  }, [open, instanceId, operationType]); // Also refresh when operationType changes (e.g., re-run)
  
  // Disable auto-refresh for completed/failed instances
  // But re-enable if status changes to in_progress (e.g., during re-run)
  useEffect(() => {
    if (status) {
      if (status.status === 'completed' || status.status === 'failed') {
        setAutoRefresh(false);
      } else if (status.status === 'in_progress' || status.status === 'pending') {
        // Re-enable auto-refresh when status changes to in_progress/pending (e.g., re-run)
        setAutoRefresh(true);
      }
    }
  }, [status?.status]);

  // Reset clearLogLength only when instanceId changes (new instance)
  useEffect(() => {
    setClearLogLength(null);
  }, [instanceId]);

  useEffect(() => {
    if (open && instanceId && autoRefresh && (status?.status === 'in_progress' || status?.status === 'pending')) {
      // Poll for updates at the configured interval if auto-refresh is enabled and in progress
      const intervalMs = refreshInterval * 1000;
      const interval = setInterval(() => {
        fetchStatus();
      }, intervalMs);

      return () => clearInterval(interval);
    }
  }, [open, instanceId, status?.status, autoRefresh, refreshInterval]);

  useEffect(() => {
    // Auto-scroll to bottom when log updates or is cleared
    if (logEndRef.current && open) {
      logEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [status?.creation_log, open, clearLogLength]);

  const fetchStatus = async () => {
    if (!instanceId) return;
    
    try {
      setLoading(true);
      const data = await connectInstanceService.getStatus(instanceId);
      setStatus(data);
    } catch (err: any) {
      console.error('Failed to fetch status', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'info';
      case 'failed':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const isComplete = status?.status === 'completed' || status?.status === 'failed';

  const getOperationTitle = () => {
    switch (operationType) {
      case 'retry':
        return isInstall ? 'Instance Installation' : 'Retry from Failed Step';
      case 'cleanup':
        return 'Cleanup & Reset';
      case null:
      case undefined:
        // If status is completed or failed, show "Install Log", otherwise "Creation Progress"
        if (isComplete) {
          return 'Install Log';
        }
        return 'Instance Creation Progress';
      default:
        return 'SSH Operation';
    }
  };

  const handleClearHistory = async () => {
    if (!instanceId || !status?.creation_log) return;
    
    try {
      setLoading(true);
      await connectInstanceService.clearLog(instanceId);
      // Refresh status to get the cleared log
      await fetchStatus();
      // Reset clearLogLength since log is now empty
      setClearLogLength(null);
    } catch (err: any) {
      console.error('Failed to clear log', err);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredLog = (log: string): string => {
    if (!clearLogLength || !log) {
      return log || '';
    }

    // If the log hasn't grown since we cleared, show nothing
    if (log.length <= clearLogLength) {
      return '';
    }

    // Show only the portion of the log that was added after clearing
    // Find where the log was when we cleared by keeping only the new portion
    // Since we're tracking by length, we'll show lines that start after that position
    const lines = log.split('\n');
    let currentLength = 0;
    const filteredLines: string[] = [];
    
    for (const line of lines) {
      // Add the line length plus newline character
      const lineWithNewline = line + '\n';
      currentLength += lineWithNewline.length;
      
      // If we've passed the clear point, include this and all subsequent lines
      if (currentLength > clearLogLength) {
        filteredLines.push(line);
      }
    }

    return filteredLines.join('\n');
  };

  const handleCopyLog = async () => {
    if (!status?.creation_log) return;
    
    const logToCopy = getFilteredLog(status.creation_log) || status.creation_log;
    
    try {
      await navigator.clipboard.writeText(logToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000); // Hide after 2 seconds
    } catch (err) {
      console.error('Failed to copy log to clipboard', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = logToCopy;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000); // Hide after 2 seconds
      } catch (fallbackErr) {
        console.error('Fallback copy failed', fallbackErr);
      }
      document.body.removeChild(textArea);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { height: '80vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{getOperationTitle()}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {status && (
              <Chip
                label={status.status}
                color={getStatusColor(status.status) as any}
                size="small"
              />
            )}
            {status?.current_step && (
              <Chip
                label={status.current_step}
                variant="outlined"
                size="small"
              />
            )}
            {/* Auto-refresh controls */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={autoRefresh}
                    onChange={(e) => setAutoRefresh(e.target.checked)}
                  />
                }
                label="Auto-refresh"
                sx={{ mr: 0, my: 0 }}
              />
              <TextField
                type="number"
                value={refreshInterval}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10);
                  if (!isNaN(val) && val > 0) {
                    setRefreshInterval(val);
                  }
                }}
                size="small"
                inputProps={{ 
                  min: 1, 
                  max: 60,
                  style: { width: '50px', textAlign: 'center', padding: '6px 4px' }
                }}
                sx={{ 
                  width: '60px', 
                  mr: 0.5,
                  my: 0,
                  '& .MuiInputBase-root': {
                    height: '32px',
                    alignItems: 'center',
                    display: 'flex',
                    margin: 0
                  },
                  '& .MuiInputBase-input': {
                    padding: '6px 4px',
                    lineHeight: '1.5',
                    display: 'flex',
                    alignItems: 'center',
                    margin: 0
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    top: 0
                  }
                }}
              />
              <Typography variant="caption" sx={{ mr: 1 }}>
                sec
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={handleClearHistory}
              disabled={!status?.creation_log}
              title="Clear log history"
            >
              <DeleteOutlineIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={fetchStatus}
              disabled={loading}
              title="Manual refresh"
            >
              <RefreshIcon 
                sx={{
                  animation: loading ? 'spin 1s linear infinite' : 'none',
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' },
                  },
                }}
              />
            </IconButton>
            <IconButton size="small" onClick={onClose} title="Close">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Error message */}
          {status?.error_message && (
            <Box sx={{ 
              mb: 2, 
              p: 2, 
              bgcolor: '#d32f2f',
              color: '#ffffff',
              borderRadius: 1,
              border: '1px solid',
              borderColor: '#c62828',
            }}>
              <Typography variant="body2" sx={{ color: '#ffffff' }}>
                <strong>Error:</strong> {status.error_message}
              </Typography>
            </Box>
          )}

          {/* Log output */}
          <Box
            sx={{
              flex: 1,
              bgcolor: 'grey.900',
              color: 'grey.100',
              p: 2,
              borderRadius: 1,
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
            }}
          >
            {/* Copy log link */}
            {status?.creation_log && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 16,
                  right: 8,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  zIndex: 1,
                }}
              >
                {copied && (
                  <Typography
                    variant="caption"
                    sx={{
                      bgcolor: 'rgba(0, 0, 0, 0.7)',
                      color: 'success.light',
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 2,
                      fontSize: '0.75rem',
                      fontWeight: 'medium',
                      animation: 'fadeInOut 2s ease-in-out',
                      '@keyframes fadeInOut': {
                        '0%': { opacity: 0, transform: 'translateY(-4px)' },
                        '10%': { opacity: 1, transform: 'translateY(0)' },
                        '90%': { opacity: 1, transform: 'translateY(0)' },
                        '100%': { opacity: 0, transform: 'translateY(-4px)' },
                      },
                    }}
                  >
                    Copied!
                  </Typography>
                )}
                <Box
                  component="button"
                  onClick={handleCopyLog}
                  sx={{
                    bgcolor: 'rgba(0, 0, 0, 0.5)',
                    color: 'grey.100',
                    border: 'none',
                    borderRadius: '20px',
                    px: 2,
                    py: 0.75,
                    fontSize: '0.75rem',
                    fontWeight: 'medium',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.7)',
                      transform: 'translateY(-1px)',
                    },
                    '&:active': {
                      transform: 'translateY(0)',
                    },
                  }}
                  title="Copy log to clipboard"
                >
                  <ContentCopyIcon sx={{ fontSize: '0.875rem' }} />
                  <Typography component="span" variant="caption" sx={{ fontSize: '0.75rem' }}>
                    Copy
                  </Typography>
                </Box>
              </Box>
            )}
            {status?.creation_log ? (
              <Box component="pre" sx={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {getFilteredLog(status.creation_log)}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Waiting for operation to start...
              </Typography>
            )}
            <div ref={logEndRef} />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SSHLogDrawer;

